import { useNavigate } from 'react-router-dom'
import { useTheme } from '../components/ThemeProvider'

const Disclaimer = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-white/60 dark:bg-sage-800/60 border-b border-sage-100/30 dark:border-sage-700/30">
        <button
          onClick={() => navigate('/home')}
          className="p-3 rounded-2xl hover:bg-sage-100/60 dark:hover:bg-sage-700/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-700/50"
        >
          <svg className="w-5 h-5 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-light text-sage-600 dark:text-sage-300">Important Information</h1>
        <div className="w-11"></div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Introduction */}
        <section className="mb-8">
          <div className="bg-white/80 dark:bg-sage-800/80 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm">
            <div className="text-center mb-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-peach-100/60 dark:bg-peach-900/30 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-peach-400 dark:text-peach-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-peach-600 dark:text-peach-300 font-light text-base sm:text-lg mb-4">Please Read Carefully</h2>
            </div>
            
            <p className="text-peach-500 dark:text-peach-400 font-light leading-relaxed text-center">
              Mind Polish is designed to support your wellbeing journey, but it's important to understand what we are and aren't.
            </p>
          </div>
        </section>

        {/* Not a Substitute */}
        <section className="mb-8">
          <div className="bg-cream-50/60 dark:bg-sage-800/60 rounded-3xl border border-cream-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-cream-600 dark:text-cream-300 font-light text-base sm:text-lg mb-6">Not a Substitute for Professional Care</h3>
            <div className="space-y-4">
              <p className="text-cream-500 dark:text-cream-400 font-light leading-relaxed">
                Mind Polish is a wellness companion, not a medical device or therapeutic treatment. While our tools can support your mental wellness journey, they cannot replace professional mental health care.
              </p>
              
              <div className="p-4 bg-cream-100/60 dark:bg-cream-900/30 rounded-2xl border border-cream-200/50 dark:border-cream-700/30">
                <p className="text-cream-600 dark:text-cream-300 font-light text-xs sm:text-sm leading-relaxed">
                  <strong>Please seek professional help if you're experiencing:</strong> Persistent depression, anxiety that interferes with daily life, thoughts of self-harm, substance abuse issues, or any mental health crisis.
                </p>
              </div>
              
              <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm leading-relaxed">
                We encourage you to use Mind Polish alongside, not instead of, professional support when needed.
              </p>
            </div>
          </div>
        </section>

        {/* About Dopa */}
        <section className="mb-8">
          <div className="bg-mint-50/60 dark:bg-sage-800/60 rounded-3xl border border-mint-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-mint-600 dark:text-mint-300 font-light text-base sm:text-lg mb-6">About Dopa (Our AI Companion)</h3>
            <div className="space-y-4">
              <p className="text-mint-500 dark:text-mint-400 font-light leading-relaxed">
                Dopa is an AI designed to provide conversational support and gentle guidance. While Dopa learns from your interactions to offer personalized responses, it's important to understand its limitations.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Dopa is <strong>conversational and reflective</strong>, not diagnostic or prescriptive
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Dopa cannot diagnose mental health conditions or provide medical advice
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Dopa's responses are generated based on patterns, not professional training
                  </p>
                </div>
              </div>
              
              <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                Think of Dopa as a supportive friend who listens and offers gentle perspectives, not as a therapist or counselor.
              </p>
            </div>
          </div>
        </section>

        {/* Emergency Resources */}
        <section className="mb-8">
          <div className="bg-peach-50/60 dark:bg-sage-800/60 rounded-3xl border border-peach-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-peach-600 dark:text-peach-300 font-light text-base sm:text-lg mb-6">Emergency Support</h3>
            <div className="space-y-4">
              <p className="text-peach-500 dark:text-peach-400 font-light leading-relaxed">
                While we provide emergency contact information within the app, please understand that these resources are not monitored by our team. We cannot provide immediate crisis intervention.
              </p>
              
              <div className="p-4 bg-peach-100/60 dark:bg-peach-900/30 rounded-2xl border border-peach-200/50 dark:border-peach-700/30">
                <div className="space-y-2">
                  <p className="text-peach-600 dark:text-peach-300 font-light text-xs sm:text-sm">
                    <strong>If you're in immediate danger:</strong>
                  </p>
                  <p className="text-peach-600 dark:text-peach-300 font-light text-xs sm:text-sm">
                    • Call 911 (US) or your local emergency number
                  </p>
                  <p className="text-peach-600 dark:text-peach-300 font-light text-xs sm:text-sm">
                    • Crisis Text Line: Text HOME to 741741
                  </p>
                  <p className="text-peach-600 dark:text-peach-300 font-light text-xs sm:text-sm">
                    • National Suicide Prevention Lifeline: 988
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use at Your Own Discretion */}
        <section className="mb-8">
          <div className="bg-sage-50/60 dark:bg-sage-800/60 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg mb-6">Use at Your Own Discretion</h3>
            <div className="space-y-4">
              <p className="text-sage-500 dark:text-sage-400 font-light leading-relaxed">
                Mind Polish is provided as a wellness tool to support your self-care journey. You are responsible for how you use the app and any decisions you make based on its content.
              </p>
              
              <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed">
                We encourage you to trust your instincts and seek additional support when you feel it would be helpful. Your wellbeing is always the priority.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section>
          <div className="bg-white/80 dark:bg-sage-800/80 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm text-center">
            <h3 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg mb-4">Questions or Concerns?</h3>
            <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed mb-6">
              If you have questions about these disclaimers or how to use Mind Polish safely, we're here to help.
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-sage-100/60 hover:bg-sage-200/50 
                         dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                         text-sage-600 dark:text-sage-300 rounded-2xl text-xs sm:text-sm font-light 
                         border border-sage-200/40 dark:border-sage-600/40 
                         hover:border-sage-300/50 dark:hover:border-sage-500/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 
                         focus:ring-sage-100/50 dark:focus:ring-sage-600/30"
            >
              Get in Touch
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Disclaimer